import Router from 'next/router'
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import { useClient, useMutation } from 'urql'

import { Loading } from '@/components/Loading'
import { Spinner } from '@/components/Spinner'
import { CopyrightText } from '@/components/copyright-text'
import { ApplicationUpdateModal } from '@/components/modal/ApplicationUpdateModal'
import { Button } from '@/components/ui/button'
import { loadAppVersion } from '@/flutter'
import { useClientOnly } from '@/hooks/useClientOnly'
import { useQuery } from '@/hooks/useQuery'
import {
  isContractSignRequiredError,
  isGuardianRequiredError,
  logout,
  resetUrqlClient,
} from '@/urql'
import { maybeGraphQLType } from '@/utils/graphql'
import { compareVersion } from '@/utils/version_number'
import { graphql } from 'gql.tada'
import { CaptureUserToSentry } from '@/components/CaptureUserToSentry'

const HOUR = 60 * 60 * 1000

interface AppSignedInLayoutProps {
  children: ReactNode
}

const maybeUser = maybeGraphQLType('User')

export function AppSignedInLayout(props: AppSignedInLayoutProps) {
  useClientOnly()
  const client = useClient()

  const [updateModalPlatform, setUpdateModalPlatform] = useState<
    null | 'android' | 'ios'
  >(null)
  const [forceUpdate, setForceUpdate] = useState<boolean>(false)
  const checkingForUpdate = useRef(false)

  useEffect(function checkUpdate() {
    if (checkingForUpdate.current) return
    checkingForUpdate.current = true

    const currentTime = Date.now()
    const lastCheckUpdateTime = getNumberFromLocalStorage('lastCheckUpdateTime')
    const lastShowUpdateModalTime = getNumberFromLocalStorage(
      'lastShowUpdateModalTime'
    )

    // only show modal once per 24 hours
    if (
      lastShowUpdateModalTime &&
      currentTime - lastShowUpdateModalTime < 24 * HOUR
    )
      return

    // only check update every 2 hours
    if (lastCheckUpdateTime && currentTime - lastCheckUpdateTime < 2 * HOUR)
      return

    saveNumberToLocalStorage('lastCheckUpdateTime', currentTime)

    Promise.all([
      loadAppVersion(),
      client.query(
        App_LatestVersion_query,
        {},
        {
          requestPolicy: 'network-only',
        }
      ),
    ])
      .then(([appVersion, latestAppVerionsResult]) => {
        const latestAppVersions = latestAppVerionsResult.data?.latestAppVersions
        if (!latestAppVersions || !appVersion) return

        const currentVersion = appVersion.app_version
        const latestVersionInfo =
          appVersion.platform == 'iOS'
            ? latestAppVersions.iosInfo
            : latestAppVersions.androidInfo

        if (compareVersion(currentVersion, latestVersionInfo.version) < 0) {
          saveNumberToLocalStorage('lastShowUpdateModalTime', currentTime)
          setUpdateModalPlatform(
            appVersion.platform == 'iOS' ? 'ios' : 'android'
          )
          setForceUpdate(latestVersionInfo.forceUpdate)
        }
      })
      .finally(() => (checkingForUpdate.current = false))
  })

  return (
    <GuardianRequiredErrorBoundary>
      <ContractSignErrorBoundary>
        <EnsuredSignedIn>
          <CaptureUserToSentry />
          {props.children}
          {updateModalPlatform && (
            <ApplicationUpdateModal
              isOpen
              onRequestClose={() => setUpdateModalPlatform(null)}
              platform={updateModalPlatform}
              forceUpdate={forceUpdate}
            />
          )}
        </EnsuredSignedIn>
      </ContractSignErrorBoundary>
    </GuardianRequiredErrorBoundary>
  )
}

function EnsuredSignedIn(props: AppSignedInLayoutProps) {
  if (!localStorage.getItem('accessToken')) {
    throw Router.replace('/app/sign_in')
  }

  const [result] = useQuery({ query: AppSignedInLayout_Query })

  const currentUser = maybeUser(result.data?.currentUser)

  if (!currentUser?.id) throw logout('/app/sign_in')

  return props.children
}

class GuardianRequiredErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: {}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: unknown) {
    if (isGuardianRequiredError(error)) {
      return { hasError: true }
    }
  }

  componentDidCatch(error: unknown, _: unknown) {
    if (!isGuardianRequiredError(error)) throw error

    resetUrqlClient()
    Router.push('/app/welcome').then(() => this.setState({ hasError: false }))
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="grid h-full place-items-center">
          <Loading />
        </div>
      )
    }

    return this.props.children
  }
}

class ContractSignErrorBoundary extends React.Component<
  { children?: ReactNode },
  { hasError: boolean }
> {
  constructor(props: {}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: unknown) {
    if (isContractSignRequiredError(error)) {
      return { hasError: true }
    }
  }

  componentDidCatch(error: unknown, _: unknown) {
    if (!isContractSignRequiredError(error)) throw error

    resetUrqlClient()
  }

  render() {
    if (this.state.hasError) {
      return (
        <ContractAgreement
          resetError={() => this.setState({ hasError: false })}
        />
      )
    }

    return this.props.children
  }
}

const AppSignedInLayout_Query = graphql(`
  query AppSignedInLayout_Query {
    currentUser {
      __typename
      ... on User {
        id
      }
    }
  }
`)

const AppContractAgreement_Query = graphql(`
  query AppContractAgreement_Query {
    currentUser {
      __typename
      ... on Node {
        id
      }
      ... on BaseUser {
        notSignedContracts {
          id
          title
          content
        }
      }
    }
  }
`)

const AppSignContractMutation = graphql(`
  mutation AppSignContractMutation($input: ContractSignInput!) {
    contractSign(input: $input) {
      contracts {
        id
        signed
      }
      errors {
        __typename
      }
    }
  }
`)

const App_LatestVersion_query = graphql(`
  query App_LatestVersion_query {
    latestAppVersions {
      iosInfo {
        version
        forceUpdate
      }
      androidInfo {
        version
        forceUpdate
      }
    }
  }
`)

function getNumberFromLocalStorage(key: string) {
  const v = window.localStorage.getItem(key)
  if (v == null) return null
  return +v
}

function saveNumberToLocalStorage(key: string, value: number) {
  window.localStorage.setItem(key, '' + value)
}

interface ContractAgreementProps {
  resetError(): void
}

function ContractAgreement(props: ContractAgreementProps) {
  const [result] = useQuery({
    query: AppContractAgreement_Query,
  })
  const notSignedContracts =
    maybeUser(result.data?.currentUser)?.notSignedContracts ?? []

  const [{ fetching: signing }, sign] = useMutation(AppSignContractMutation)

  async function handleSignContracts() {
    const { error, data } = await sign({
      input: { id: notSignedContracts?.map((contract) => contract.id) },
    })

    if (error || data?.contractSign?.errors?.length) {
      return toast.error('失敗しました', {
        toastId: 'contract-sign-failed',
        position: 'bottom-right',
      })
    }

    resetUrqlClient()
    props.resetError()
  }

  return (
    <main className="flex min-h-screen flex-col bg-[#F5F7FC]">
      <div className="flex grow flex-col">
        <section className="grid h-full place-items-center">
          <div className="mt-[75px]">
            <h2 className="mx-6 mb-5 max-w-[320px] text-center text-xl font-bold leading-normal">
              利用規約・プライバシーポリシー
            </h2>
            <div className="mx-auto max-w-[280px]">
              <article
                tabIndex={0}
                className="flex h-[272px] flex-col gap-10 overflow-y-auto rounded-md border border-transparent bg-white p-4"
              >
                {notSignedContracts?.map((contract) => (
                  <div key={contract.id}>
                    <h2 className="mb-[14px] text-center font-bold text-[#828488]">
                      {contract.title}
                    </h2>

                    <div className="policy-section">
                      <div
                        className="text-justify text-xs text-[#919191]"
                        dangerouslySetInnerHTML={{ __html: contract.content }}
                      />
                    </div>
                  </div>
                ))}
              </article>
              <p className="mt-2 text-xs font-medium tracking-wider text-[#828488]">
                利用規約およびプライバシーポリシーを確認し、同意の上で次へ進んでください。
              </p>
              <Button
                variant="secondary"
                className="mt-[25px] w-full"
                shadow={true}
                onClick={handleSignContracts}
              >
                {signing ? <Spinner /> : '同意する'}
              </Button>
            </div>
          </div>
        </section>
      </div>
      <footer className="mt-auto py-3">
        <p className="text-center text-xs text-[#C4C6CB]">
          <CopyrightText />
        </p>
      </footer>
    </main>
  )
}

export function getAppSignedInLayout(page: ReactElement) {
  return <AppSignedInLayout>{page}</AppSignedInLayout>
}
